/*----------------------------------
- DEPENDANCES
----------------------------------*/

// Npm
import React from 'react';
import type { ComponentChild } from 'preact';
import { useErrorBoundary } from 'preact/hooks';

// Core
import useContext, { ClientContext } from '@/client/context';

// Core components
import RouterComponent from '@client/services/router/components/router';
import { Button, Link } from '@client/components';

// app
import AnnouncementsModal from './AnnouncementsModal';

// Resources
import "./index.less";
import Logo from '@/client/assets/identity/logoAndTextBlack.png';

/*----------------------------------
- COMPOSANT
----------------------------------*/
export default function App ({ context, menu }: { 
    context: ClientContext,
    menu: ComponentChild
 }) {

    /*----------------------------------
    - INIT
    ----------------------------------*/

    const { Router, api, modal, user, app, toast } = context;

    const [openMenu, setOpenMenu] = React.useState<'user'|'navig'>();
    context.setOpenMenu = setOpenMenu;
    const [stats, setStats] = React.useState<TUserStats>({});
    context.stats = stats;
    
    // Render bug report
    // TIDO: move to framework
    useErrorBoundary( e => {
        app.reportBug({
            before: 'Render page',
            stacktrace: e.stack
        }).then(() => {

            // Reload page
            if (__DEV__) {
                console.warn(`The app would be reloaded in production mode.`);
                toast.error(`Errur during page rendering: ${e.message}`);
                console.error(e);
            } else 
                toast.error(`An issue occurred while loading this page. Our dev team will solve it asap.`);
        })
    });

    /*----------------------------------
    - ACTIONS
    ----------------------------------*/

    React.useEffect(() => {

        loadAnnouncements();

        loadUserStats();

    }, []);

    const loadAnnouncements = () => api.get('/feedbacks/announcements').then( announcements => {
        if (announcements.length > 0)
            modal.show(AnnouncementsModal, { announcements });
    })

    const loadUserStats = () => api.get('/user/stats').then(setStats);

    /*----------------------------------
    - RENDER
    ----------------------------------*/

    const csmMenu: ComponentChild[] = []
    if (user?.roles.includes('CSM')) csmMenu.push(
        <Button nav link="/csm/missions" icon="crosshairs">
            Missions
        </Button>,
        <Button nav link="/csm/talents" icon="users">
            Candidates
        </Button>
    );

    return (
        <div id="layout" class={"dashboard" + (openMenu ? ' ' + openMenu + 'MenuOpen' : '')}
            onClick={(e) => {
                if (openMenu && e.target?.id === 'page')
                    setOpenMenu(null)
            }}>

            <header id="sidebar" class="bg white col scrollable">

                <div id="identity" />

                <nav class="row-1">

                    <ul class="menu col al-top">

                        <Button nav="exact" icon="lightbulb" link="/">
                            <strong class="fg primary">Start Here 🚀</strong> 
                        </Button>

                        <Button nav="exact" link="/recruiter/missions" icon="crosshairs" iconR={stats.missions > 0 && (
                            <span class="badge s bg info light">
                                {stats.missions}
                            </span>
                        )}>
                            Missions
                        </Button>

                        <Button nav link={(true || user?.roles.includes('CSM')) ? '/csm/missions/create' : '/recruiter/missions/submit'} 
                            icon="plus-circle">
                            Add a Mission
                        </Button>

                        {/* <Button nav link="/talents/ats" icon="user-circle" iconR={(
                            <div class="s badge bg info light">
                                {stats.talents}
                            </div>
                        )}>
                            My Talents
                        </Button> */}

                        <Button disabled nav link="/team" icon="star" iconR={(
                            <div class="s badge bg info light">
                                {stats.teamMembers}
                            </div>
                        )}>
                            My Team
                        </Button>
                    </ul>


                    {user?.credits > 0 && <>
                        <strong>Prospector (BETA)</strong>
                        <ul class="menu col al-top">

                            {/* <Button nav="exact" link="/sales" icon="home" iconR={stats.prospectIssues > 0 && (
                                <div class="s badge bg error">
                                    {stats.prospectIssues}
                                </div>
                            )}>
                                Overview
                            </Button> */}

                            <Button nav link="/sales/campaigns" icon="paper-plane" iconR={stats.prospectCampaigns > 0 && (
                                <div class="s badge bg info light">
                                    {stats.prospectCampaigns}
                                </div>
                            )}>
                                Campaigns
                            </Button>

                            <Button nav link="/sales/tasks" icon="check-circle" iconR={stats.prospectTasks > 0 && (
                                <div class="s badge bg info">
                                    {stats.prospectTasks}
                                </div>
                            )}>
                                Tasks
                            </Button>

                            {/* <Button nav link="/sales/companies" icon="building" iconR={(
                                <div class="s badge bg info">
                                    0
                                </div>
                            )}>
                                Companies
                            </Button> */}

                            <Button nav link="/sales/contacts" icon="user-circle" iconR={stats.prospectContacts > 0 && (
                                <div class="s badge bg info light">
                                    {stats.prospectContacts}
                                </div>
                            )}>
                                Contacts
                            </Button>

                            <Button nav link="/sales/prospect" icon="search" suffix={(
                                <span class="badge bg warn light s">
                                    Beta
                                </span>
                            )}>
                                Search
                            </Button>

                            {/* <Button nav link="/sales/alerts" icon="binoculars" iconR={stats.jobAlerts > 0 ? (
                                <span class="badge s bg info light">
                                    {stats.jobAlerts}
                                </span>
                            ) : (
                                <div class="s badge bg info">
                                    New
                                </div>
                            )}>
                                Job Alerts
                            </Button> */}
                        </ul>
                    </>}

                    {csmMenu.length > 0 && <>
                        <strong>CSM Area</strong>
                        <ul class="menu col al-top">
                            {csmMenu}
                        </ul>
                    </>}

                    {user?.roles.includes('ADMIN') && <>
                        <strong>Admin</strong>
                        <ul class="menu col al-top">

                            <Button nav link="/admin/headhunters" icon="user-circle">
                                Headhunters
                            </Button>

                            <Button nav link="/admin/issues" icon="bug" iconR={stats.prospectIssues > 0 && (
                                <div class="s badge bg error">
                                    {stats.prospectIssues}
                                </div>
                            )}>
                                Issues
                            </Button>

                            <Button nav link="/bizdev/missions" icon="crosshairs" iconR={stats.missionsToReview > 0 && (
                                <span class="badge s bg error">
                                    {stats.missionsToReview}
                                </span>
                            )}>
                                Missions
                            </Button>

                            <Button nav link="/admin/csms" icon="binoculars">
                                CSMs <i class="s fg error" src="solid/crown" />
                            </Button>
                        </ul>
                    </>}

                    <strong>Help</strong>
                    <ul class="menu col al-top">
                        <Button nav link="/recruiter/campus" icon="rocket">
                            Campus (🇫🇷 only)
                        </Button>
                        <Button link="/help/terms" icon="file">
                            Terms and Conditions
                        </Button>
                    </ul>
                </nav>

                <Button type="primary" class="mgb-05" link="/user/invite">
                    Invite Recruiters
                </Button>

            </header>

            <RouterComponent service={Router} />
        </div>
    )
}